import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { EventService } from '@app/modules/events/services/event.service'
import { ApiKeyCardComponent, ApiKeyEventOption } from '@app/v2/settings/components/api-keys/api-key-card/api-key-card.component'
import { CreateApiKeyModalComponent } from '@app/v2/settings/components/api-keys/create-api-key-modal/create-api-key-modal.component'
import { ApiToken } from '@app/v2/settings/enums/api-tokens.model'
import { ApiKeyService } from '@app/v2/settings/services/api-key.service'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { InfiniteScrollModule } from 'ngx-infinite-scroll'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { catchError, EMPTY, finalize, Subject, switchMap, tap } from 'rxjs'
import { CopyApiKeyModalComponent } from './copy-api-key-modal/copy-api-key-modal.component'

@Component({
  selector: 'app-api-keys',
  imports: [TranslateModule, InfiniteScrollModule, CreateApiKeyModalComponent, CopyApiKeyModalComponent, ApiKeyCardComponent, NgxSkeletonLoaderModule, DeleteModalComponent],
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss'],
  providers: [ApiKeyService]
})
export class ApiKeysComponent implements OnInit {
  public readonly apiKeys = signal<ApiToken[]>([])
  public readonly events = signal<ApiKeyEventOption[]>([])
  public readonly isLoadingEvents = signal(false)
  public readonly isLoadingApiKeys = signal(false)
  public readonly isLoadingMore = signal(false)
  public readonly createModalId = 'create-api-key-modal'
  public readonly editModalId = 'edit-api-key-modal'
  public readonly createdModalId = 'copy-api-key-modal'
  public readonly deleteModalId = 'delete-api-key-modal'
  public readonly rotateModalId = 'rotate-api-key-modal'
  public readonly createdToken = signal<string | null>(null)
  public readonly copyModalTitleKey = signal('v2.orgSettings.apiKeys.createApiKeySuccess')
  public readonly editingTokenId = signal<number | null>(null)
  public readonly pendingDeletion = signal<ApiToken | null>(null)
  public readonly pendingRotation = signal<ApiToken | null>(null)
  public readonly workspaceName = computed(() => this._authService.currentWorkspace?.name ?? '')
  public readonly emptyStateSrc = 'assets/images/v2/api-keys-empty.svg'

  public readonly showInitialEmptyState = computed(() => !this.isLoadingApiKeys() && this.apiKeys().length === 0)
  public readonly infiniteScrollDisabled = computed(() => !this._hasMore() || this.isLoadingApiKeys() || this.isLoadingMore())
  public readonly deleteConfirmMessage = computed(() => {
    const token = this.pendingDeletion()
    if (!token) return ''

    return `${this._translate.instant('v2.orgSettings.apiKeys.deleteConfirm', { name: token.name })}<br>${this._translate.instant('v2.orgSettings.apiKeys.deleteConfirmHint')}`
  })
  public readonly rotateConfirmMessage = computed(() => {
    if (!this.pendingRotation()) return ''
    return this._translate.instant('v2.orgSettings.apiKeys.rotateConfirm')
  })

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _authService = inject(AuthService)
  private readonly _eventService = inject(EventService)
  private readonly _modalService = inject(ModalService)
  private readonly _alertService = inject(AlertService)
  private readonly _translate = inject(TranslateService)
  private readonly _apiKeyService = inject(ApiKeyService)
  private readonly _organizationService = inject(OrganizationService)

  private _page = 1
  private readonly _hasMore = signal(true)
  private readonly _perPage = 3
  private readonly _load$ = new Subject<boolean>()

  ngOnInit(): void {
    this._setupApiKeyLoading()
    this._loadEvents()
    this._load$.next(true)
  }

  public openCreateApiKeyModal(): void {
    this._modalService.toggleModal(this.createModalId)
  }

  public closeCreateApiKeyModal(): void {
    this._closeModal(this.createModalId)
  }

  public isModalOpened(id: string): boolean {
    return this._modalService.isModalOpen(id)
  }

  public onApiKeyCreated({ token, name }: { token: string; name: string }): void {
    this.closeCreateApiKeyModal()
    this._load$.next(true)
    this._alertService.alert(Alert.SUCCESS, `${name} ${this._translate.instant('v2.orgSettings.apiKeys.createApiKeyToast')}`)

    if (!token) return

    this._openCopyModal(token, 'v2.orgSettings.apiKeys.createApiKeySuccess')
  }

  public onEditRequested(token: ApiToken): void {
    this.editingTokenId.set(token.id)
    this._openModal(this.editModalId)
  }

  public closeEditApiKeyModal(): void {
    this._closeModal(this.editModalId)
    this.editingTokenId.set(null)
  }

  public onApiKeyUpdated(updated: ApiToken | null): void {
    const name = updated?.name ?? ''
    this.closeEditApiKeyModal()

    if (updated) this._replaceApiKey(updated)
    else this._load$.next(true)

    this._alertService.alert(Alert.SUCCESS, `${name} ${this._translate.instant('v2.orgSettings.apiKeys.updateSuccess')}`)
  }

  public onDeleteRequested(token: ApiToken): void {
    this.pendingDeletion.set(token)
    this._openModal(this.deleteModalId)
  }

  public confirmApiKeyDeletion(): void {
    const token = this.pendingDeletion()
    if (!token) return

    this._apiKeyService
      .deleteApiKey(token.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.apiKeys.update((keys) => keys.filter((item) => item.id !== token.id))
          this.pendingDeletion.set(null)
          this._closeModal(this.deleteModalId)
          this._alertService.alert(Alert.SUCCESS, `${token.name} ${this._translate.instant('v2.orgSettings.apiKeys.deleteSuccess')}`)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.apiKeys.deleteError'))
        }
      })
  }

  public cancelApiKeyDeletion(): void {
    this.pendingDeletion.set(null)
    this._closeModal(this.deleteModalId)
  }

  public onRotateRequested(token: ApiToken): void {
    this.pendingRotation.set(token)
    this._openModal(this.rotateModalId)
  }

  public confirmApiKeyRotation(): void {
    const token = this.pendingRotation()
    if (!token) return

    this._apiKeyService
      .rotateApiKey(token.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          if (response.data) this._replaceApiKey(response.data)
          this.pendingRotation.set(null)
          this._closeModal(this.rotateModalId)
          this._alertService.alert(Alert.SUCCESS, `${token.name} ${this._translate.instant('v2.orgSettings.apiKeys.rotateSuccess')}`)

          if (!response.token) return

          this._openCopyModal(response.token, 'v2.orgSettings.apiKeys.rotateApiKeySuccess')
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.apiKeys.rotateError'))
        }
      })
  }

  public cancelApiKeyRotation(): void {
    this.pendingRotation.set(null)
    this._closeModal(this.rotateModalId)
  }

  public closeCreatedApiKeyModal(): void {
    this._closeModal(this.createdModalId)
    this.createdToken.set(null)
    this.copyModalTitleKey.set('v2.orgSettings.apiKeys.createApiKeySuccess')
  }

  public onApiKeysListScroll(): void {
    if (this.infiniteScrollDisabled()) return
    this._load$.next(false)
  }

  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  private _openCopyModal(token: string, titleKey: string): void {
    this.copyModalTitleKey.set(titleKey)
    this.createdToken.set(token)
    this._openModal(this.createdModalId)
  }

  private _replaceApiKey(updated: ApiToken): void {
    this.apiKeys.update((keys) => keys.map((item) => (item.id === updated.id ? updated : item)))
  }

  private _openModal(id: string): void {
    if (!this._modalService.isModalOpen(id)) {
      this._modalService.toggleModal(id)
    }
  }

  private _closeModal(id: string): void {
    if (this._modalService.isModalOpen(id)) {
      this._modalService.toggleModal(id)
    }
  }

  private _setupApiKeyLoading(): void {
    this._load$
      .pipe(
        switchMap((reset) => this._loadApiKeysPage(reset)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe()
  }

  private _loadApiKeysPage(reset: boolean) {
    if (reset) {
      this._page = 1
      this._hasMore.set(true)
      this.apiKeys.set([])
    }

    const organizationId = this._authService.currentWorkspace?.id
    if (!this._hasMore() || !organizationId) return EMPTY

    this.isLoadingApiKeys.set(reset)
    this.isLoadingMore.set(!reset)

    return this._apiKeyService.getApiKeys(organizationId, { page: this._page, per_page: this._perPage }).pipe(
      tap((result) => {
        this.apiKeys.update((current) => (reset ? result.items : [...current, ...result.items]))
        this._hasMore.set(result.current_page < result.last_page)
        this._page = result.current_page + 1
      }),
      catchError((error) => {
        this._hasMore.set(false)
        if (reset) this.apiKeys.set([])
        this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.apiKeys.loadApiKeysError'))
        return EMPTY
      }),
      finalize(() => {
        this.isLoadingApiKeys.set(false)
        this.isLoadingMore.set(false)
      })
    )
  }

  private _loadEvents(): void {
    this.isLoadingEvents.set(true)
    this._eventService
      // Fetch events across all statuses (live/upcoming/finished). We still exclude archived events.
      .getEvents(1, null, 0, false, 50, null)
      .pipe(
        finalize(() => this.isLoadingEvents.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => this.events.set(this._extractEventOptions(response)),
        error: (error) => {
          this.events.set([])
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.apiKeys.loadEventsError'))
        }
      })
  }

  /**
   * Maps the events API payload into `{ id, name, slug }` options for the Limited-access event picker.
   * Accepts a bare array or `{ data: [] }`. Rows without a valid id or name are dropped.
   * @param response Events API body
   * @returns Options for the event `select-input`
   */
  private _extractEventOptions(response: unknown): ApiKeyEventOption[] {
    const rawEvents = Array.isArray(response) ? response : (response as { data?: unknown[] })?.data ?? []

    return rawEvents
      .map((event) => {
        const record = event as { id?: number | string; name?: string; slug?: string }
        const id = Number(record.id)
        const name = record.name?.trim() ?? ''
        const slug = record.slug?.trim() ?? ''

        if (Number.isNaN(id) || !name) return null

        return { id, name, slug }
      })
      .filter((event): event is ApiKeyEventOption => event !== null)
  }
}
